import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import Layout from "../../components/layout/Layout";
import instantOrdersApi from "../../api/instantOrders";
import useAuth from "../../hooks/useAuth";

const statusOptions = ["DELIVERED", "CANCELLED"] as const;

const statusColors: Record<
  string,
  "default" | "primary" | "warning" | "success" | "error" | "info"
> = {
  PLACED: "default",
  ACCEPTED: "primary",
  PREPARING: "warning",
  READY: "info",
  PICKED_UP: "primary",
  DELIVERED: "success",
  CANCELLED: "error",
};

const InstantOrders = () => {
  const { admin } = useAuth();
  const isAdminUser = admin?.role === "admin" || admin?.role === "super-admin";
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await instantOrdersApi.getOrders({
        page,
        limit: 20,
        search: debouncedSearch || undefined,
        status: status || undefined,
        date: date || undefined,
      });
      setOrders(data.orders || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (fetchError) {
      console.error("Failed to fetch instant orders", fetchError);
      setError("Could not fetch Kinnow Instant orders");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, status, date]);

  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

	  const stats = useMemo(() => {
	    return {
	      total: orders.length,
	      delivered: orders.filter((order) => order.status === "DELIVERED").length,
	      cancelled: orders.filter((order) => order.status === "CANCELLED").length,
	      refunds: orders.filter((order) => ["refund_pending", "refund_initiated", "refund_failed", "refunded"].includes(order.paymentStatus)).length,
	    };
	  }, [orders]);

  return (
    <Layout>
      <Box>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant="h4" fontWeight={800}>
              Instant Order History
            </Typography>
            <Typography color="text.secondary">
              Review completed and cancelled Kinnow Instant orders.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void fetchOrders()}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          <Card sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                History items on page
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Delivered
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {stats.delivered}
              </Typography>
            </CardContent>
          </Card>
	          <Card sx={{ flex: 1, borderRadius: 3 }}>
	            <CardContent>
	              <Typography variant="body2" color="text.secondary">
	                Refund cases
	              </Typography>
	              <Typography variant="h5" fontWeight={800}>
	                {stats.refunds}
	              </Typography>
	            </CardContent>
	          </Card>
        </Stack>

        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                placeholder="Order number or address"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="date"
                label="Date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Items</TableCell>
                        {isAdminUser && (
                          <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          View
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order._id} hover>
                          <TableCell>
                            <Typography fontWeight={700}>
                              {order.orderNumber}
                            </Typography>
                            <Stack direction="row" spacing={1} mt={0.5}>
                              <Chip
                                size="small"
                                icon={<HistoryIcon />}
                                label={order.kitchenId?.name || "Kitchen"}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                icon={<HistoryIcon />}
                                label={new Date(
                                  order.createdAt,
                                ).toLocaleDateString()}
                                variant="outlined"
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={600}>
                              {order.userId?.name || "Unknown"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {order.userId?.phoneNumber || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>
                              {order.items?.length || 0} meals
                            </Typography>
                            {!!order.addOns?.length && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {order.addOns.length} add-ons
                              </Typography>
                            )}
                          </TableCell>
                          {isAdminUser && (
                            <TableCell>
                              <Typography fontWeight={700}>
                                Rs {order.totalPaid}
                              </Typography>
	                              <Typography
	                                variant="body2"
	                                color={order.paymentStatus === "refund_failed" ? "error.main" : "text.secondary"}
	                              >
	                                {order.paymentStatus === "refund_initiated"
	                                  ? `refund initiated${order.refundAmount ? ` • Rs ${order.refundAmount}` : ""}`
	                                  : order.paymentStatus === "refund_failed"
	                                    ? "refund needs attention"
	                                    : order.paymentMethod}
	                              </Typography>
	                            </TableCell>
	                          )}
	                          <TableCell>
	                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
	                              <Chip
	                                size="small"
	                                label={order.status}
	                                color={statusColors[order.status] || "default"}
	                              />
	                              {order.paymentStatus === "refund_initiated" && (
	                                <Chip size="small" label="Refund initiated" color="warning" variant="outlined" />
	                              )}
                              {order.paymentStatus === "refund_pending" && (
                                <Chip size="small" label="Refund pending" color="warning" variant="outlined" />
                              )}
	                              {order.paymentStatus === "refund_failed" && (
	                                <Chip size="small" label="Refund attention" color="error" variant="outlined" />
	                              )}
	                              {order.paymentStatus === "refunded" && (
	                                <Chip size="small" label="Refunded" color="success" variant="outlined" />
	                              )}
	                            </Stack>
	                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="text"
                              onClick={() => setSelectedOrder(order)}
                            >
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {orders.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                            No Instant Order history found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box display="flex" justifyContent="center" py={3}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={Boolean(selectedOrder)}
          onClose={() => setSelectedOrder(null)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Order Details</DialogTitle>
          <DialogContent dividers>
            {selectedOrder && (
              <Stack spacing={2}>
                <Typography fontWeight={700}>
                  {selectedOrder.orderNumber}
                </Typography>
                <Typography color="text.secondary">
                  {selectedOrder.deliveryAddress?.addressLine}
                </Typography>
                <Typography>
                  Customer: {selectedOrder.userId?.name}{" "}
                  {selectedOrder.userId?.phoneNumber
                    ? `• ${selectedOrder.userId.phoneNumber}`
                    : ""}
                </Typography>
	                <Typography>
	                  {isAdminUser
	                    ? `Total Paid: Rs ${selectedOrder.totalPaid} • `
	                    : ""}
	                  Payment: {selectedOrder.paymentMethod}
	                </Typography>
	                {["refund_pending", "refund_initiated", "refund_failed", "refunded"].includes(selectedOrder.paymentStatus) && (
	                  <Alert severity={selectedOrder.paymentStatus === "refund_failed" ? "error" : "warning"}>
	                    <Typography fontWeight={700}>
	                      {selectedOrder.paymentStatus === "refund_failed"
	                        ? "Refund needs attention"
	                        : selectedOrder.paymentStatus === "refunded"
	                          ? "Refund processed"
	                          : selectedOrder.paymentStatus === "refund_pending" ? "Refund pending" : "Refund initiated"}
	                    </Typography>
	                    <Typography variant="body2">
	                      Amount: Rs {selectedOrder.refundAmount || 0}
	                      {selectedOrder.refundGateway ? ` • Gateway: ${selectedOrder.refundGateway}` : ""}
	                      {selectedOrder.refundGatewayId ? ` • Refund ID: ${selectedOrder.refundGatewayId}` : ""}
	                    </Typography>
	                    {selectedOrder.cancellationReason && (
	                      <Typography variant="body2">
	                        Reason: {selectedOrder.cancellationReason}
	                      </Typography>
	                    )}
	                    {selectedOrder.refundError && (
	                      <Typography variant="body2">
	                        Error: {selectedOrder.refundError}
	                      </Typography>
	                    )}
	                  </Alert>
	                )}
                {selectedOrder.riderId && (
                  <Stack spacing={0.5}>
                    <Typography>
                      Rider: {selectedOrder.riderId.name}{" "}
                      {selectedOrder.riderId.phoneNumber
                        ? `• ${selectedOrder.riderId.phoneNumber}`
                        : ""}
                    </Typography>
                    {selectedOrder.deliveryMode && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Delivery Method:{" "}
                          <strong>
                            {selectedOrder.deliveryMode === "hand_delivery"
                              ? "🤝 Hand Delivery"
                              : "📦 Doorstep Drop"}
                          </strong>
                        </Typography>
                        {selectedOrder.deliveryPhotoUrl && (
                          <Button
                            component="a"
                            href={selectedOrder.deliveryPhotoUrl}
                            target="_blank"
                            rel="noreferrer"
                            size="small"
                            variant="outlined"
                            sx={{ py: 0.25, px: 1, minWidth: 0, textTransform: "none", fontSize: "0.75rem" }}
                          >
                            📸 View Photo
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Stack>
                )}
                {selectedOrder.notes && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: "#fffbeb",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="warning.main"
                      fontWeight={850}
                    >
                      Instructions / Special Note:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#78350f" }}
                    >
                      {selectedOrder.notes}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography fontWeight={700} mb={1}>
                    Items
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      ...(selectedOrder.items || []),
                      ...(selectedOrder.addOns || []),
                    ].map((item: any, index: number) => (
                      <Paper
                        key={`${item.instantMealId}-${index}`}
                        variant="outlined"
                        sx={{ p: 1.5 }}
                      >
                        <Stack direction="row" justifyContent="space-between">
                          <Box>
                            <Typography fontWeight={600}>
                              {item.name}
                            </Typography>
                            {item.selectedSide && (
                              <Typography
                                variant="body2"
                                color="warning.main"
                                sx={{ fontWeight: 650, mt: 0.25, mb: 0.25 }}
                              >
                                Option: {item.selectedSide.name} (
                                {item.selectedSide.price > 0
                                  ? `+Rs ${item.selectedSide.price}`
                                  : "Free"}
                                )
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              Qty {item.quantity}{" "}
                              {isAdminUser && `• Rs ${item.price} each`}
                            </Typography>
                          </Box>
                          {isAdminUser && (
                            <Typography fontWeight={700}>
                              Rs {item.totalPrice}
                            </Typography>
                          )}
                        </Stack>

                        {item.itemTypeRef === "NowCombo" && Array.isArray(item.comboItems) && item.comboItems.length > 0 && (
                          <Box sx={{ pl: 2, mt: 1, borderLeft: "2px dashed #e67e22" }}>
                            {item.comboItems.map((sub: any, subIdx: number) => (
                              <Typography key={subIdx} variant="caption" sx={{ display: "block", color: "text.primary", fontWeight: 600 }}>
                                └─ {sub.quantity > 1 ? `${sub.quantity}x ` : ""}{sub.name}
                                {sub.selectedSide?.name && <span style={{ color: "#d97706" }}> ({sub.selectedSide.name})</span>}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
                <Box>
                  <Typography fontWeight={700} mb={1}>
                    Timeline
                  </Typography>
                  <Stack spacing={1}>
                    {(selectedOrder.statusTimeline || []).map(
                      (event: any, index: number) => (
                        <Paper
                          key={`${event.status}-${index}`}
                          variant="outlined"
                          sx={{ p: 1.5 }}
                        >
                          <Typography fontWeight={600}>
                            {event.status}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(event.timestamp).toLocaleString()} •{" "}
                            {event.updatedBy || "system"}
                          </Typography>
                          {event.note && (
                            <Typography variant="body2" mt={0.5}>
                              {event.note}
                            </Typography>
                          )}
                        </Paper>
                      ),
                    )}
                  </Stack>
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedOrder(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default InstantOrders;
